import { Button } from "@garther/ui/components";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { trackEngagement } from "@/libs/analytics";
import { firebaseService } from "@/libs/firebase";

import { useAuthStore } from "../../stores/auth.store";

interface FriendInvitationAuthProps {
  onSuccess: () => void;
  onError: (message: string) => void;
}

export const FriendInvitationAuth: React.FC<FriendInvitationAuthProps> = ({ onSuccess, onError }) => {
  const { t } = useTranslation();
  const { loggedIn, loading } = useAuthStore();
  const [isSigningIn, setIsSigningIn] = useState(false);

  const handleSignIn = async () => {
    setIsSigningIn(true);
    try {
      trackEngagement("sign_in_click", "authentication");
      firebaseService.signIn();
    } catch (error) {
      onError(t("auth.signInFailed"));
    } finally {
      setIsSigningIn(false);
    }
  };

  // Listen for auth state changes
  React.useEffect(() => {
    if (loggedIn && !loading) {
      onSuccess();
    }
  }, [loggedIn, loading, onSuccess]);

  return (
    <div className="h-full e-center bg-background-primary">
      <div className="max-w-md w-full mx-4">
        <div className="bg-background-secondary rounded-sm shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="heading-2 text-text-primary mb-4">{t("friendInvitation.authTitle")}</h1>
            <p className="body-base text-text-secondary">{t("friendInvitation.authDescription")}</p>
          </div>

          <div className="space-y-4">
            <div className="bg-background-highlight rounded-sm p-4 border border-border-light">
              <h3 className="heading-5 text-text-primary mb-2">{t("friendInvitation.whatHappensNext")}</h3>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">1.</span>
                  {t("friendInvitation.step1")}
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">2.</span>
                  {t("friendInvitation.step2")}
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">3.</span>
                  {t("friendInvitation.step3")}
                </li>
              </ul>
            </div>
            <p className="text-center text-xs text-text-secondary">{t("friendInvitation.privacyNote")}</p>
          </div>

          <div className="mt-8 pt-6 border-t border-border-light">
            <div className="text-center">
              <Button variant="default" onClick={handleSignIn} disabled={isSigningIn} className="mt-2 w-full">
                {t("navigation.login")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
